import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {GripVerticalIcon, PencilIcon, PlusIcon, SaveIcon, Trash2Icon} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";

type OutletCtx = { eventId: string | null };

type FeedbackCollectionMode = "EMAIL_POST_EVENT" | "EXIT_SCAN" | "BOTH";
type FeedbackQuestionType = "TEXT" | "RANGE" | "BOOLEAN" | "SINGLE_CHOICE" | "MULTIPLE_CHOICE" | "GRID";

type ConfigForm = {
    enabled: boolean;
    collectionMode: FeedbackCollectionMode;
    emailDelayDays: string;
};

type QuestionForm = {
    id?: string;
    text: string;
    questionType: FeedbackQuestionType;
    required: boolean;
    position: number;
    options: string;
};

const COLLECTION_MODES: Array<{ value: FeedbackCollectionMode; label: string }> = [
    {value: "EMAIL_POST_EVENT", label: "Email after event"},
    {value: "EXIT_SCAN", label: "Exit scan"},
    {value: "BOTH", label: "Both"},
];

const QUESTION_TYPES: Array<{ value: FeedbackQuestionType; label: string }> = [
    {value: "TEXT", label: "Text"},
    {value: "RANGE", label: "Range (1–10)"},
    {value: "BOOLEAN", label: "Yes / No"},
    {value: "SINGLE_CHOICE", label: "Single choice"},
    {value: "MULTIPLE_CHOICE", label: "Multiple choice"},
    {value: "GRID", label: "Grid"},
];

function parseGridOptions(options: string): { rows: string; columns: string } {
    try {
        const parsed = JSON.parse(options);
        return {
            rows: (parsed.rows ?? []).join(", "),
            columns: (parsed.columns ?? []).join(", "),
        };
    } catch {
        return {rows: "", columns: ""};
    }
}

function serializeGridOptions(rows: string, columns: string): string {
    return JSON.stringify({
        rows: rows.split(",").map((s) => s.trim()).filter(Boolean),
        columns: columns.split(",").map((s) => s.trim()).filter(Boolean),
    });
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
}

function normalizePositions(list: QuestionForm[]): QuestionForm[] {
    return list.map((q, idx) => ({...q, position: idx + 1}));
}

function toInt(v: string): number {
    const n = Number((v ?? "").replace(/[^\d]/g, ""));
    return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
}

export function FeedbackStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [savingQuestion, setSavingQuestion] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const [dirtyOrder, setDirtyOrder] = useState(false);

    const [config, setConfig] = useState<ConfigForm>({
        enabled: false,
        collectionMode: "EMAIL_POST_EVENT",
        emailDelayDays: "3",
    });

    const [questions, setQuestions] = useState<QuestionForm[]>([]);
    const [editor, setEditor] = useState<QuestionForm | null>(null);
    const [gridRows, setGridRows] = useState("");
    const [gridCols, setGridCols] = useState("");

    useEffect(() => {
        if (editor?.questionType === "GRID") {
            const g = parseGridOptions(editor.options);
            setGridRows(g.rows);
            setGridCols(g.columns);
        }
    }, [editor?.id, editor?.questionType]);

    const dragFrom = useRef<number | null>(null);

    useEffect(() => {
        if (!businessId || !eventId) return;

        setLoading(true);
        (async () => {
            try {
                const [cfgRes, qRes] = await Promise.all([
                    api.get(`/api/v1/business/${businessId}/events/${eventId}/feedback`),
                    api.get(`/api/v1/business/${businessId}/events/${eventId}/feedback/questions`),
                ]);

                const cfg = cfgRes.data;
                setConfig({
                    enabled: Boolean(cfg?.enabled ?? false),
                    collectionMode: cfg?.collectionMode ?? "EMAIL_POST_EVENT",
                    emailDelayDays: String(cfg?.emailDelayDays ?? 3),
                });

                setQuestions(
                    normalizePositions(
                        (qRes.data ?? [])
                            .map((q: any) => ({
                                id: q.id,
                                text: q.text ?? "",
                                questionType: q.questionType ?? "TEXT",
                                required: Boolean(q.required ?? false),
                                position: q.position ?? 0,
                                options: q.options ?? "",
                            }))
                            .sort((a: QuestionForm, b: QuestionForm) => a.position - b.position)
                    )
                );

                setDirtyOrder(false);
            } catch (e) {
                notify.error(getErrMsg(e, "Failed to load feedback configuration"));
            } finally {
                setLoading(false);
            }
        })();
    }, [businessId, eventId]);

    const saveConfig = async () => {
        if (!businessId || !eventId) return;

        setSavingConfig(true);
        try {
            await api.patch(`/api/v1/business/${businessId}/events/${eventId}/feedback`, {
                enabled: config.enabled,
                collectionMode: config.collectionMode,
                emailDelayDays: config.enabled ? toInt(config.emailDelayDays) : null,
            });
            notify.success("Feedback settings saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save feedback settings"));
        } finally {
            setSavingConfig(false);
        }
    };

    const upsertQuestion = async (q: QuestionForm) => {
        if (!businessId || !eventId) return;

        setSavingQuestion(true);
        try {
            const {data} = await api.post(
                `/api/v1/business/${businessId}/events/${eventId}/feedback/questions`,
                {
                    id: q.id ?? null,
                    text: q.text.trim(),
                    questionType: q.questionType,
                    required: q.required,
                    position: q.position,
                    options: (q.questionType === "SINGLE_CHOICE" || q.questionType === "MULTIPLE_CHOICE" || q.questionType === "GRID") ? (q.options ?? "") : "",
                }
            );

            const saved: QuestionForm = {
                id: data.id,
                text: data.text,
                questionType: data.questionType,
                required: Boolean(data.required),
                position: data.position,
                options: data.options ?? "",
            };

            setQuestions((prev) => {
                const idx = prev.findIndex((x) => x.id === saved.id);
                if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = saved;
                    return next;
                }
                return normalizePositions([...prev, saved]);
            });

            setDirtyOrder(true);
            notify.success("Question saved");
            setEditor(null);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save question"));
        } finally {
            setSavingQuestion(false);
        }
    };

    const deleteQuestion = async (q: QuestionForm) => {
        if (!businessId || !eventId) return;

        if (!q.id) {
            setQuestions((prev) => normalizePositions(prev.filter((x) => x !== q)));
            return;
        }

        if (!confirm(`Delete question "${q.text}"?`)) return;

        try {
            await api.delete(
                `/api/v1/business/${businessId}/events/${eventId}/feedback/questions/${q.id}`
            );
            setQuestions((prev) => normalizePositions(prev.filter((x) => x.id !== q.id)));
            notify.success("Question deleted");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to delete question"));
        }
    };

    const saveOrder = async () => {
        if (!businessId || !eventId) return;

        setSavingOrder(true);
        try {
            await Promise.all(
                questions
                    .filter((q) => !!q.id)
                    .map((q) =>
                        api.post(
                            `/api/v1/business/${businessId}/events/${eventId}/feedback/questions`,
                            {
                                id: q.id,
                                text: q.text,
                                questionType: q.questionType,
                                required: q.required,
                                position: q.position,
                                options: q.options,
                            }
                        )
                    )
            );
            setDirtyOrder(false);
            notify.success("Order saved");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save question order"));
        } finally {
            setSavingOrder(false);
        }
    };

    const onDragStart = (idx: number) => {
        dragFrom.current = idx;
    };
    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };
    const onDrop = (toIdx: number) => {
        const fromIdx = dragFrom.current;
        dragFrom.current = null;
        if (fromIdx == null || fromIdx === toIdx) return;

        setQuestions((prev) => normalizePositions(moveItem(prev, fromIdx, toIdx)));
        setDirtyOrder(true);
    };

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit")
            navigate(`/business/events/${encodeURIComponent(eventId)}/edit/options`);
        else
            navigate(`/business/events/new/options?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    // ---- Question editor view (full-page, replaces main view)
    if (editor) {
        const needsOptions = editor.questionType === "SINGLE_CHOICE" || editor.questionType === "MULTIPLE_CHOICE";
        const isGrid = editor.questionType === "GRID";
        const canSave = editor.text.trim().length > 0 && !savingQuestion;

        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {editor.id ? "Edit question" : "New question"}
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Define a question attendees will answer in the post-event feedback form.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            onClick={() => setEditor(null)}
                            disabled={savingQuestion}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={() => upsertQuestion(editor)}
                            disabled={!canSave}
                        >
                            <span className="inline-flex items-center gap-2">
                                <SaveIcon className="size-4"/>
                                {savingQuestion ? "Saving…" : "Save question"}
                            </span>
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="md:col-span-3">
                        <div className="text-sm font-medium text-gray-900">Question text *</div>
                        <input
                            value={editor.text}
                            onChange={(e) => setEditor({...editor, text: e.target.value})}
                            placeholder="e.g. How would you rate the event overall?"
                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                        />
                    </div>

                    <div>
                        <div className="text-sm font-medium text-gray-900">Question type</div>
                        <select
                            value={editor.questionType}
                            onChange={(e) =>
                                setEditor({...editor, questionType: e.target.value as FeedbackQuestionType})
                            }
                            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                        >
                            {QUESTION_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="md:col-span-2 flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4">
                        <div>
                            <div className="text-sm font-semibold text-gray-900">Required</div>
                            <div className="text-xs text-gray-600">Attendees must answer this question to submit.</div>
                        </div>
                        <input
                            type="checkbox"
                            checked={editor.required}
                            onChange={(e) => setEditor({...editor, required: e.target.checked})}
                            className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                        />
                    </div>

                    {needsOptions && (
                        <div className="md:col-span-3">
                            <div className="text-sm font-medium text-gray-900">Options</div>
                            <div className="mt-1 text-xs text-gray-500">
                                Comma-separated choices (e.g. Great, Good, Average, Poor)
                            </div>
                            <input
                                value={editor.options}
                                onChange={(e) => setEditor({...editor, options: e.target.value})}
                                placeholder="e.g. Great, Good, Average, Poor"
                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                            />
                        </div>
                    )}

                    {isGrid && (
                        <>
                            <div className="md:col-span-3">
                                <div className="text-sm font-medium text-gray-900">Rows</div>
                                <div className="mt-1 text-xs text-gray-500">
                                    Comma-separated row labels (e.g. Food, Crafts, Alcohol)
                                </div>
                                <input
                                    value={gridRows}
                                    onChange={(e) => {
                                        setGridRows(e.target.value);
                                        setEditor({
                                            ...editor,
                                            options: serializeGridOptions(e.target.value, gridCols),
                                        });
                                    }}
                                    placeholder="e.g. Food, Crafts, Alcohol beverages"
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                />
                            </div>
                            <div className="md:col-span-3">
                                <div className="text-sm font-medium text-gray-900">Columns</div>
                                <div className="mt-1 text-xs text-gray-500">
                                    Comma-separated column labels (e.g. 0, &lt;N$100, N$100 - N$199)
                                </div>
                                <input
                                    value={gridCols}
                                    onChange={(e) => {
                                        setGridCols(e.target.value);
                                        setEditor({
                                            ...editor,
                                            options: serializeGridOptions(gridRows, e.target.value),
                                        });
                                    }}
                                    placeholder="e.g. 0, <N$100, N$100 - N$199"
                                    className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ---- Main view
    const showEmailDelay =
        config.collectionMode === "EMAIL_POST_EVENT" || config.collectionMode === "BOTH";

    return (
        <div className="space-y-6">
            <SectionHeading
                title={mode === "edit" ? "Edit Feedback Settings" : "Feedback Settings"}
                description="Collect post-event feedback from your attendees via email or exit scan."
                actions={
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={saveConfig}
                        disabled={savingConfig}
                    >
                        {savingConfig ? "Saving…" : (
                            <span className="inline-flex items-center gap-2">
                                <SaveIcon className="size-4"/>Save
                            </span>
                        )}
                    </Button>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading feedback settings…</div>
            ) : (
                <>
                    {/* Config card */}
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
                            <h3 className="text-sm font-semibold text-gray-900">Feedback collection</h3>
                        </div>

                        <div className="p-5 space-y-6">
                            {/* Enable toggle */}
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-sm font-medium text-gray-900">
                                        Enable feedback collection
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">
                                        Attendees will be invited to complete a feedback survey after the event.
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={config.enabled}
                                    onClick={() => setConfig((p) => ({...p, enabled: !p.enabled}))}
                                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${
                                        config.enabled ? "bg-orange-500" : "bg-gray-200"
                                    }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                                            config.enabled ? "translate-x-5" : "translate-x-0"
                                        }`}
                                    />
                                </button>
                            </div>

                            {/* Collection mode */}
                            {config.enabled && (
                                <div>
                                    <div className="text-sm font-medium text-gray-900 mb-3">
                                        Collection method
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        {COLLECTION_MODES.map((m) => (
                                            <button
                                                key={m.value}
                                                type="button"
                                                onClick={() =>
                                                    setConfig((p) => ({...p, collectionMode: m.value}))
                                                }
                                                className={`rounded-lg border px-5 py-3 text-sm font-medium transition-colors ${
                                                    config.collectionMode === m.value
                                                        ? "border-orange-600 bg-orange-50 text-orange-700"
                                                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                                }`}
                                            >
                                                {m.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Email delay */}
                            {config.enabled && showEmailDelay && (
                                <div>
                                    <div className="text-sm font-medium text-gray-900">
                                        Send email (days after event)
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        How many days after the event to send the feedback email.
                                    </p>
                                    <input
                                        type="number"
                                        min={0}
                                        value={config.emailDelayDays}
                                        onChange={(e) =>
                                            setConfig((p) => ({...p, emailDelayDays: e.target.value}))
                                        }
                                        className="mt-2 w-32 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-600 focus:outline-none"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Questions card */}
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="border-b border-gray-200 bg-gray-50 px-5 py-4 flex items-center justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-gray-900">Feedback questions</div>
                                <div className="mt-1 text-xs text-gray-600">
                                    Add questions attendees will answer. Drag to reorder.
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {dirtyOrder && (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="md"
                                        onClick={saveOrder}
                                        disabled={savingOrder}
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <SaveIcon className="size-4"/>
                                            {savingOrder ? "Saving…" : "Save order"}
                                        </span>
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="md"
                                    onClick={() =>
                                        setEditor({
                                            text: "",
                                            questionType: "TEXT",
                                            required: false,
                                            position: questions.length + 1,
                                            options: "",
                                        })
                                    }
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <PlusIcon className="size-4"/>Add question
                                    </span>
                                </Button>
                            </div>
                        </div>

                        <ul className="divide-y divide-gray-100">
                            {questions.length === 0 ? (
                                <li className="px-5 py-10 text-center text-sm text-gray-500">
                                    No questions yet. Click <span className="font-medium">Add question</span>.
                                </li>
                            ) : questions.map((q, idx) => (
                                <li
                                    key={q.id ?? `new-${idx}`}
                                    draggable
                                    onDragStart={() => onDragStart(idx)}
                                    onDragOver={onDragOver}
                                    onDrop={() => onDrop(idx)}
                                    className="px-5 py-4 hover:bg-gray-50/60"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className="mt-0.5 flex items-center gap-2 text-gray-500">
                                                <GripVerticalIcon className="size-4"/>
                                                <span className="text-xs">{q.position}</span>
                                            </div>

                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <div className="truncate text-sm font-semibold text-gray-900">
                                                        {q.text}
                                                    </div>
                                                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-100">
                                                        {QUESTION_TYPES.find((t) => t.value === q.questionType)?.label ?? q.questionType}
                                                    </span>
                                                    {q.required && (
                                                        <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-semibold text-orange-700 ring-1 ring-orange-100">
                                                            Required
                                                        </span>
                                                    )}
                                                </div>
                                                {(q.questionType === "SINGLE_CHOICE" || q.questionType === "MULTIPLE_CHOICE") && q.options && (
                                                    <div className="mt-1 text-xs text-gray-500">
                                                        Options: {q.options}
                                                    </div>
                                                )}
                                                {q.questionType === "GRID" && q.options && (() => {
                                                    const grid = parseGridOptions(q.options);
                                                    return (
                                                        <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                                                            {grid.rows && <div>Rows: {grid.rows}</div>}
                                                            {grid.columns && <div>Columns: {grid.columns}</div>}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => setEditor({...q})}
                                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                            >
                                                <PencilIcon className="size-4"/>
                                                <span className="hidden sm:inline">Edit</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => deleteQuestion(q)}
                                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                                            >
                                                <Trash2Icon className="size-4"/>
                                                <span className="hidden sm:inline">Delete</span>
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={() => navigate("/business/events")}
                >
                    Exit
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={goNext}
                    disabled={savingConfig}
                >
                    Continue to Options
                </Button>
            </div>
        </div>
    );
}
